import * as yup from 'yup';
import {i18n2} from 'features/i18n/i18n';
import {hasNumber, hasSymbol, hasObviousSequence} from './PasswordValidations';

export const buildPasswordSchema = (settings: any) => {
  // All validations should be skipped when editing and the password value
  // is equal to the original encrypted value provided by the backend
  const skipIfUnchanged = function (this: any, value: any) {
    const original =
      (this && this.parent && this.parent.passwordOriginal) || null;
    if (original && value === original) return true;
    return false;
  };

  let schema: any = yup.string().nullable();

  // Required only when adding (no passwordOriginal) or when changed
  schema = schema.test(
    'required-if-changed',
    i18n2._('Este campo es obligatorio'),
    function (this: any, value: any) {
      const original =
        (this && this.parent && this.parent.passwordOriginal) || null;
      if (original) {
        if (value === original) return true; // unchanged -> not required
        return !!value; // changed -> must have value
      }
      // no original -> creation flow -> required
      return !!value;
    },
  );

  // Max length only when changed/creating
  schema = schema.test(
    'max-if-changed',
    i18n2._('La contraseña debe tener como máximo 25 caracteres'),
    function (this: any, value: any) {
      if (skipIfUnchanged.call(this, value)) return true;
      if (!value) return true;
      return value.length <= 25;
    },
  );

  if (settings.pwdRequireMinLength) {
    schema = schema.test(
      'min-length-if-changed',
      i18n2._(
        `La contraseña debe tener al menos ${settings.pwdMinLength} caracteres`,
      ),
      function (this: any, value: any) {
        if (skipIfUnchanged.call(this, value)) return true;
        if (!value) return true; // required test handles empty case
        return value.length >= settings.pwdMinLength;
      },
    );
  }

  if (settings.pwdRequireNumber) {
    schema = schema.test(
      'has-number-if-changed',
      i18n2._('La contraseña debe contener al menos un número'),
      function (this: any, value: any) {
        if (skipIfUnchanged.call(this, value)) return true;
        if (!value) return true;
        return hasNumber(value);
      },
    );
  }

  if (settings.pwdRequireSymbol) {
    schema = schema.test(
      'has-symbol-if-changed',
      i18n2._('La contraseña debe contener al menos un símbolo'),
      function (this: any, value: any) {
        if (skipIfUnchanged.call(this, value)) return true;
        if (!value) return true;
        return hasSymbol(value);
      },
    );
  }

  if (settings.pwdDisallowObviousSequences) {
    schema = schema.test(
      'no-obvious-sequence-if-changed',
      i18n2._('La contraseña contiene secuencias obvias'),
      function (this: any, value: any) {
        if (skipIfUnchanged.call(this, value)) return true;
        if (!value) return true;
        return !hasObviousSequence(value);
      },
    );
  }

  if (settings.pwdDisallowUserData) {
    schema = schema.test(
      'no-user-data-if-changed',
      i18n2._('La contraseña no debe contener datos personales'),
      function (this: any, value: any) {
        if (skipIfUnchanged.call(this, value)) return true;
        if (!value) return true;

        const v = value.toLowerCase();
        const {cwid, name, email} = this.parent || {};

        if (cwid && v.includes(cwid.toLowerCase()))
          return this.createError({
            message: i18n2._('La contraseña no debe contener datos personales'),
          });
        if (email && v.includes(email.toLowerCase()))
          return this.createError({
            message: i18n2._('La contraseña no debe contener datos personales'),
          });

        if (name) {
          const parts = name.toLowerCase().split(' ');
          if (parts.some((p: any) => p.length > 2 && v.includes(p)))
            return this.createError({
              message: i18n2._(
                'La contraseña no debe contener datos personales',
              ),
            });
        }

        return true;
      },
    );
  }

  return schema.label(i18n2._('Contraseña'));
};
